import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { REVIEWS } from "@/lib/site";
import { api } from "@/lib/api";

export const ReviewsCarousel = () => {
  const [index, setIndex] = useState(0);
  const [reviews, setReviews] = useState(REVIEWS);

  useEffect(() => {
    api.get("/reviews")
      .then((r) => {
        if (r.data?.length) {
          setReviews(r.data.map((v) => ({
            quote: v.quote,
            author: v.name,
            detail: v.vehicle || "Verified Customer",
            rating: v.rating || 5,
          })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const review = reviews[index % reviews.length];

  return (
    <div className="relative" data-testid="reviews-carousel">
      <Quote className="h-10 w-10 text-trx-blue/40" />
      <div className="mt-6 min-h-[180px] md:min-h-[150px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex gap-1" data-testid="review-stars">
              {Array.from({ length: review.rating || 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-trx-blue text-trx-blue" />
              ))}
            </div>
            <p className="mt-5 font-display text-2xl md:text-3xl font-medium leading-snug tracking-tight text-white">
              “{review.quote}”
            </p>
            <p className="mt-5 text-sm text-zinc-500">
              <span className="font-semibold text-zinc-300">{review.author}</span> — {review.detail}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button
          onClick={() => setIndex((index - 1 + reviews.length) % reviews.length)}
          data-testid="review-prev"
          aria-label="Previous review"
          className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-400 transition-colors duration-300 hover:border-trx-blue hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setIndex((index + 1) % reviews.length)}
          data-testid="review-next"
          aria-label="Next review"
          className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-400 transition-colors duration-300 hover:border-trx-blue hover:text-white"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <div className="ml-3 flex gap-2">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`review-dot-${i}`}
              aria-label={`Review ${i + 1}`}
              className={`h-1 transition-all duration-500 ${i === index ? "w-10 bg-trx-blue" : "w-4 bg-white/20"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
